// src/components/MedicalBillingAudit.js
import React, { useState } from 'react';
import './MedicalBillingAudit.css';

const MedicalBillingAudit = () => {
  const [formData, setFormData] = useState({
    service: '',
    stage: '',
    fullName: '',
    phone: '',
    email: '',
    website: '',
    reachVia: '',
    preferredTime: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const serviceOptions = [
    'Medical Billing Audit',
    'Medical Billing',
    'Medical Coding',
    'Credentialing',
    'Denial Management',
    'AR Follow-up'
  ];

  const stageOptions = ['Pre-Billing Audit', 'Post-Billing Audit', 'Coding Audit', 'Compliance Audit', 'Full RCM Audit'];
  const reachViaOptions = ['Email', 'Phone', 'Both'];
  const timeOptions = ['Morning (9AM - 12PM)', 'Afternoon (12PM - 4PM)', 'Evening (4PM - 7PM)', 'Anytime'];

  const auditServices = [
    { title: 'Pre-Billing Audits', desc: 'We review claims before submission to identify potential errors and compliance issues.' },
    { title: 'Post-Billing & Payment Audits', desc: 'We analyze submitted and paid claims to ensure correct reimbursement.' },
    { title: 'Coding & Documentation Audits', desc: 'Our auditors assess coding accuracy and documentation sufficiency to minimize audit risk.' },
    { title: 'Denial & AR Audits', desc: 'We identify denial patterns and determine that impact cash flow.' },
    { title: 'Compliance & Risk Audits', desc: 'We help practices prepare for payer or regulatory audits.' }
  ];

  const specialties = [
    'Neurology', 'Cardiology', 'Nephrology', 'Pediatric',
    'Podiatry', 'Allergy', 'Mental Health', 'Dermatology'
  ];

  const benefits = [
    'Identify and recover lost revenue',
    'Reduce claim denial and rejection',
    'Improve operating efficiency',
    'Improve coding and billing accuracy',
    'Strengthen compliance and reduce audit risk',
    'Increase payer confidence and reimbursement accuracy'
  ];

  const whyChooseFeatures = [
    'Experienced billing and coding auditors',
    'Specialty-specific audits expertise',
    'HIPAA-compliant and secure processes',
    'Clear, actionable audit reports',
    'Practical recommendations—not just findings',
    'Proven revenue cycle management best practices'
  ];

  const processSteps = [
    'Initial assessment and goal alignment',
    'Secure data access and sample selection',
    'Detailed claim, coding, and payment review',
    'Identification of errors and risk areas',
    'Comprehensive audit report delivery',
    'Action plan and improvement recommendations'
  ];

  const reportingFeatures = [
    'Error summaries and trends',
    'Financial impact analysis',
    'Key compliance risk areas',
    'Step-by-step corrective actions'
  ];

  const whoShouldConsider = [
    'Practice experiencing high denial rates',
    'Providers concerned about compliance',
    'New practices or recent EHR transitions',
    'Organizations preparing for payer or CMS audits',
    'Practice changing billing vendors'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you shortly.' });
        setFormData({ service: '', stage: '', fullName: '', phone: '', email: '', website: '', reachVia: '', preferredTime: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: '', message: '' }), 5000);
    }
  };

  return (
    <main className="audit-page">
      {/* Hero Section */}
    {/* Hero Section */}
<section className="audit-hero">
  <div className="container">
    <div className="audit-hero-content">
      <h1 className="audit-hero-title">
        Professional Medical Billing Audit Services <span className="highlight">for Smarter Billing</span>
      </h1>
      <p className="audit-hero-description">
        Most revenue loss in healthcare comes from billing errors like missing modifiers, coding mistakes, or under-coded 
        E/M services. Our medical billing audit service at Providers Care Billing LLC reviews claims and notes to identify 
        errors, recover lost revenue, and improve billing accuracy.
      </p>
    </div>
  </div>
</section>

      {/* Consultation Form Section */}
      <section className="audit-consultation">
        <div className="container">
          <div className="audit-form-wrapper">
            <h2 className="audit-form-title">Get a free Consultation</h2>
            {submitStatus.message && <div className={`status-message ${submitStatus.type}`}>{submitStatus.message}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <select name="service" value={formData.service} onChange={handleChange} required>
                    <option value="">Select Service</option>
                    {serviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="stage" value={formData.stage} onChange={handleChange} required>
                    <option value="">Select Stage</option>
                    {stageOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="text" name="fullName" placeholder="Full Name" value={formData.fullName} onChange={handleChange} required /></div>
                <div className="form-group"><input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required /></div>
                <div className="form-group"><input type="url" name="website" placeholder="Website/Caption" value={formData.website} onChange={handleChange} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <select name="reachVia" value={formData.reachVia} onChange={handleChange} required>
                    <option value="">Reach Out Via</option>
                    {reachViaOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} required>
                    <option value="">Preferred Time</option>
                    {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="audit-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Get Started'}</button>
            </form>
          </div>
        </div>
      </section>

      {/* Efficient Audit Section */}
      <section className="audit-efficient">
        <div className="container">
          <div className="efficient-content">
            <h2 className="efficient-title">Efficient Medical Billing Audit for All Practices</h2>
            <p className="efficient-subtitle">
              Ensure Billing Compliance with Professional Medical Billing Audits Across the USA! Maximize revenue, reduce denials, 
              and stay HIPAA-compliant with Providers Care Billing LLC's nationwide medical billing audit service.
            </p>
            <div className="efficient-grid">
              <div className="efficient-item">✓ Coding and billing errors</div>
              <div className="efficient-item">✓ Underpayments and overpayments</div>
              <div className="efficient-item">✓ Compliance risks</div>
              <div className="efficient-item">✓ Process inefficiencies</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Audit Services Section */}
      <section className="audit-services">
        <div className="container">
          <div className="audit-services-header">
            <h2 className="section-title-dark">Our Medical Billing Audit Services</h2>
            <p className="section-subtitle-dark">
              Our Medical Billing Audit Services are designed to support your growth at every stage, from small practices to 
              multi-specialty operations. Here are just a few of our Medical Billing Audit Services:
            </p>
          </div>
          <div className="audit-services-grid">
            {auditServices.map((service, index) => (
              <div className="audit-service-card" key={index}>
                <div className="service-icon">📋</div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Based Section */}
      <section className="audit-specialty">
        <div className="container">
          <div className="audit-specialty-content">
            <h2 className="audit-specialty-title">Specialty Based Medical Billing Audits</h2>
            <p className="audit-specialty-text">
              Our medical billing services are designed to support your growth at every stage, from established practices to 
              multi-site expansions. Here are just a few services that we provide under this.
            </p>
            <div className="specialty-tags">
              {specialties.map(spec => <span key={spec} className="specialty-tag">{spec}</span>)}
            </div>
            <button className="view-specialties-btn">View All Specialties →</button>
          </div>
        </div>
      </section>

      {/* Trusted Services Section */}
      <section className="audit-trusted">
        <div className="container">
          <div className="trusted-content">
            <h2>Trusted Medical Billing Auditing Services in the USA</h2>
            <p>Whether you need a one-time audit or ongoing auditing support, Providers Care Billing LLC delivers accurate, confidential, and actionable medical billing auditing services to protect your revenue and ensure compliance.</p>
            <button className="trusted-btn">Schedule An Appointment →</button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="audit-benefits">
        <div className="container">
          <div className="benefits-wrapper-audit">
            <h2 className="benefits-main-title">Benefits of Medical Billing Auditing</h2>
            <p className="benefits-subtitle">
              Healthcare organizations lose billions every month because of inefficient billing practices. At Providers Care Billing LLC, 
              we offer comprehensive medical billing auditing services to help reduce these losses and improve accuracy.
            </p>
            <div className="benefits-grid-audit">
              {benefits.map((benefit, index) => (
                <div className="benefit-card-audit" key={index}>
                  <span className="benefit-check">✓</span>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="audit-why-choose">
        <div className="container">
          <h2 className="why-choose-title">Why Choose Providers Care Billing LLC For Your Medical Billing Audit</h2>
          <p className="why-choose-subtitle">
            Providers Care Billing LLC is a trusted medical billing audit company with experienced audit experts dedicated to 
            helping you prevent costly penalties and enhance revenue streams, while ensuring a strong focus on high-quality patient care.
          </p>
          <div className="why-choose-grid-audit">
            {whyChooseFeatures.map((feature, index) => (
              <div className="why-choose-card" key={index}>
                <span className="why-check">✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="audit-process">
        <div className="container">
          <h2 className="process-main-title">Our Medical Billing Audit Process</h2>
          <p className="process-subtitle">
            Provider Care Billing LLC conducts thorough billing audits to identify and correct errors, ensuring a transparent 
            and reliable revenue cycle process.
          </p>
          <div className="process-steps-audit">
            {processSteps.map((step, index) => (
              <div className="process-step-audit" key={index}>
                <span className="step-number-audit">{index + 1}</span>
                <span className="step-text-audit">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reporting Section */}
      <section className="audit-reporting">
        <div className="container">
          <div className="reporting-content">
            <h2 className="reporting-title">Clear Reporting & Actionable Insights</h2>
            <p className="reporting-text">
              Our medical billing audit services ensure comprehensive reports that include:
            </p>
            <div className="reporting-grid">
              {reportingFeatures.map((feature, index) => (
                <div className="reporting-item" key={index}>
                  <span className="reporting-check">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who Should Consider Section */}
      <section className="audit-who-consider">
        <div className="container">
          <h2 className="consider-title">Who Should Consider a Medical Billing Audit?</h2>
          <div className="consider-grid">
            {whoShouldConsider.map((item, index) => (
              <div className="consider-item" key={index}>
                <span className="consider-check">✔</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="audit-cta">
        <div className="container">
          <div className="cta-content-audit">
            <h2>Schedule a Free Medical Billing Audit Consultation Today</h2>
            <button className="cta-audit-btn">Schedule An Appointment →</button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MedicalBillingAudit;